import { JudgmentResult, JudgmentResultType, JudgmentResultEnum } from "../../types.client";
import { RsrpFmRadioMeasure } from "../../../models/schemas/measures/radioWave.schema";
import { RsrpBaseController } from "./RsrpBaseController";
import { RSRP_FM_RADIO_THRESHOLDS } from "../../constants";

/**
 * 14. RSRP FM라디오 컨트롤러
 *
 * 판정 기준:
 * - 아날로그: SNR ≥ 50dB
 * - 디지털: SNR ≥ 20dB, MER ≥ 20dB
 * - RSSI ≥ -75dBm
 *
 * Note: 아날로그/디지털 구분은 config.extra.mode로 설정
 */
export class RsrpFmRadioController extends RsrpBaseController<RsrpFmRadioMeasure> {
  protected getDefaultData(): RsrpFmRadioMeasure {
    return {
      snr: "",
      mer: "",
      ber: "",
      rssi: "",
      result: null,
      discussion: "",
    };
  }

  protected judge(): JudgmentResult {
    const snr = this.parseSnr();
    const mer = this.parseMer();
    const rssi = this.parseRssi();

    // 측정값이 하나도 없으면 NA
    if (snr === null && mer === null && rssi === null) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: this.generateDiscussion(JudgmentResultEnum.NA),
      };
    }

    let result: JudgmentResultType = JudgmentResultEnum.GOOD;
    const isAnalog = this.config.extra?.mode === "analog";

    // SNR 판정 (아날로그: ≥50dB, 디지털: ≥20dB)
    if (snr !== null) {
      const snrThreshold = isAnalog
        ? RSRP_FM_RADIO_THRESHOLDS.snr.analog
        : RSRP_FM_RADIO_THRESHOLDS.snr.digital;
      if (snr < snrThreshold) {
        result = JudgmentResultEnum.BAD;
      }
    }

    // MER 판정 (디지털 모드 전용: ≥20dB)
    if (mer !== null && !isAnalog) {
      if (mer < RSRP_FM_RADIO_THRESHOLDS.mer.digital) {
        result = JudgmentResultEnum.BAD;
      }
    }

    // RSSI 판정 (≥-75dBm)
    if (rssi !== null && rssi < RSRP_FM_RADIO_THRESHOLDS.rssi.min) {
      result = JudgmentResultEnum.BAD;
    }

    return {
      result,
      discussion: this.generateDiscussion(result),
    };
  }

  protected generateDiscussion(result: JudgmentResultType): string {
    const snr = this.parseSnr();
    const mer = this.parseMer();
    const rssi = this.parseRssi();

    if (result === JudgmentResultEnum.NA) {
      return "FM라디오 RSRP 측정값이 입력되지 않았습니다.";
    }

    const isAnalog = this.config.extra?.mode === "analog";
    const mode = isAnalog ? "아날로그" : "디지털";
    const snrThreshold = isAnalog
      ? RSRP_FM_RADIO_THRESHOLDS.snr.analog
      : RSRP_FM_RADIO_THRESHOLDS.snr.digital;

    const parts: string[] = [];
    if (snr !== null) {
      const snrStatus = snr >= snrThreshold ? "양호" : "불량";
      parts.push(`SNR ${snr}dB (${mode} 기준 ≥${snrThreshold}dB, ${snrStatus})`);
    }
    // MER 추가 (디지털 모드 전용)
    if (mer !== null && !isAnalog) {
      const merThreshold = RSRP_FM_RADIO_THRESHOLDS.mer.digital;
      const merStatus = mer >= merThreshold ? "양호" : "불량";
      parts.push(`MER ${mer}dB (디지털 기준 ≥${merThreshold}dB, ${merStatus})`);
    }
    if (rssi !== null) {
      const rssiStatus = rssi >= RSRP_FM_RADIO_THRESHOLDS.rssi.min ? "양호" : "불량";
      parts.push(`RSSI ${rssi}dBm (기준 ≥${RSRP_FM_RADIO_THRESHOLDS.rssi.min}dBm, ${rssiStatus})`);
    }

    return parts.join(", ");
  }
}
